import { z } from "zod";

const US_PHONE_HINT = "Enter a valid US phone number (e.g. (555) 123-4567)";

/**
 * Accepts common US formats — (555) 123-4567, 555-123-4567, 555.123.4567,
 * 5551234567, +1 555 123 4567. Strips non-digits, drops an optional leading
 * country code (1), then requires a 10-digit number whose area code does not
 * start with 0 or 1 (NANP).
 */
const isUsPhone = (value: string): boolean => {
  const digits = value.replace(/\D/g, "");
  const national =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return /^[2-9]\d{9}$/.test(national);
};

/**
 * Validation for the self-service "Account details" form (`PATCH /v1/users/me`).
 * Email, role and region are NOT here — they are read-only server-side (§D.3).
 * Every editable field is mandatory; `departmentId` is the empty string when
 * unset (controlled select) and a positive int once chosen.
 */
export const profileFormSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, "First name is required")
    .max(30, "First name must be 30 characters or fewer"),
  lastName: z
    .string()
    .trim()
    .min(1, "Last name is required")
    .max(30, "Last name must be 30 characters or fewer"),
  phone: z
    .string()
    .trim()
    .min(1, "Telephone number is required")
    .max(32, "Phone number must be 32 characters or fewer")
    .refine(isUsPhone, US_PHONE_HINT),
  companyName: z
    .string()
    .trim()
    .min(1, "Company name is required")
    .max(100, "Company name must be 100 characters or fewer"),
  companyAddress: z
    .string()
    .trim()
    .min(1, "Company address is required")
    .max(500, "Company address must be 500 characters or fewer"),
  departmentId: z
    .union([z.literal(""), z.coerce.number().int().positive()])
    .optional(),
}).superRefine((data, ctx) => {
  if (data.departmentId === "" || data.departmentId == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["departmentId"],
      message: "Function / Department is required",
    });
  }
});

export type ProfileFormData = z.infer<typeof profileFormSchema>;
